import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

type FeedbackType = "like" | "dislike" | null;

export function useUserFeedback(searchQuery?: string) {
  const { user } = useAuth();
  const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackType>>({});

  useEffect(() => {
    if (!user) {
      setFeedbackMap({});
      return;
    }
    supabase
      .from("user_feedback")
      .select("episode_id, feedback_type")
      .eq("user_id", user.id)
      .then(({ data }) => {
        if (data) {
          const map: Record<string, FeedbackType> = {};
          data.forEach((r) => {
            map[r.episode_id] = r.feedback_type as FeedbackType;
          });
          setFeedbackMap(map);
        }
      });
  }, [user]);

  // Optimistic: paint the new feedback immediately, then sync. If the write
  // fails we restore the prior value so the thumbs match reality.
  const toggleFeedback = useCallback(
    async (episodeId: string, type: "like" | "dislike") => {
      if (!user) return null;
      const previous = feedbackMap[episodeId] ?? null;
      const next: FeedbackType = previous === type ? null : type;

      setFeedbackMap((prev) => ({ ...prev, [episodeId]: next }));

      let error: any = null;
      if (next === null) {
        ({ error } = await supabase
          .from("user_feedback")
          .delete()
          .eq("user_id", user.id)
          .eq("episode_id", episodeId));
      } else if (previous) {
        ({ error } = await supabase
          .from("user_feedback")
          .update({ feedback_type: next, search_query: searchQuery || null })
          .eq("user_id", user.id)
          .eq("episode_id", episodeId));
      } else {
        ({ error } = await supabase.from("user_feedback").insert({
          user_id: user.id,
          episode_id: episodeId,
          feedback_type: next,
          search_query: searchQuery || null,
        }));
      }

      if (error) {
        setFeedbackMap((prev) => ({ ...prev, [episodeId]: previous }));
        toast.error("Couldn't save your feedback.");
        return previous;
      }
      return next;
    },
    [user, feedbackMap, searchQuery]
  );

  return { feedbackMap, toggleFeedback };
}
